import Dexie,{Table} from 'dexie';

// Define the types for each table
interface Project {
  id: number;
  name: string;
  permittee: string;
  notes: string;
  routine_interval: string;
  timezone: string;
  inspector_user_id: string;
  document_group: string;
  start_date: string;
  end_date: string;
  inspection_compliance_required: boolean;
  cgp_tracking_id: string;
  next_inspection_date: string;
  archivedAt: string;
  findingComplianceRequired: boolean;
  isCcrProject: boolean;
  precipitation_flag: boolean;
  precipitation_threshold: number;
  snow_threshold: number;
  project_type_id: number;
  inspection_interval_id: number;
  precipitation_edit_lock: boolean;
  primary_contact_user_id: string;
  external_project: string;
  work_order: string;
  purchase_order: string;
  finding_metric_tracking: boolean;
  dissolve_finding_images: boolean;
  enable_photo_metadata_requirement: boolean;
  forecast_weather: string;
  current_acreage: number;
  client: string;
  address: string;
  inspections: any[];
  downloadedOn: string;
}

interface Inspection {
  id: number;
  project_id: number;
  GUID: string;
  template_id: string;
  created_date: string;
  inspection_types: {type:string}[];
  facility_name: string;
  client_name: string;
  cms_inspector_name: string;
  inspector_id: string;
  compliance_id: string;
  municipality: string;
  certification_notes: string;
  certification_date: string;
  acknowledgement_date: string;
  compliance_date: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  project: any;
  users: any[];
  inspection_template: any;
  findings: any[];
  questions: any[];
  certifications: any[];
  inspectionNumber:number;
}

interface User {
  id: number;
  user_id:number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  role: string;
}

// Define your DB structure
interface Database {
  projects: Table<Project, number>;  // Table with type 'Project' and primary key 'number'
  inspections: Table<Inspection, number>;  // Table with type 'Inspection' and primary key 'number'
  findings: Table<any, number>;
  questions: Table<any, number>;
  certifications: Table<any, number>;
  inspectionNotes: Table<any, number>;
  findingTypeInspectionTemplate: Table<any, number>;
  user: Table<User, number>;
  images: Table<any, number>;
  inspectionUploads: Table<any, number>;
  commentPayload: Table<any, number>;
  inspectionTemplate: Table<any, number>;
  positionables: Table<any, number>;
  projectInspectionUploads: Table<any, number>;
}

const db = new Dexie('SW2OfflineDB') as Dexie & Database;

db.version(2).stores({
  projects: `
    id,
    name,
    permittee,
    client_id,
    address_id,
    inspection_template_id,
    notes,
    routine_interval,
    timezone,
    inspector_user_id,
    document_group,
    start_date,
    end_date,
    inspection_compliance_required,
    cgp_tracking_id,
    next_inspection_date,
    archivedAt,
    findingComplianceRequired,
    isCcrProject,
    precipitation_flag,
    precipitation_threshold,
    snow_threshold,
    project_type_id,
    inspection_interval_id,
    precipitation_edit_lock,
    primary_contact_user_id,
    external_project,
    work_order,
    purchase_order,
    finding_metric_tracking,
    dissolve_finding_images,
    enable_photo_metadata_requirement,
    forecast_weather,
    current_acreage,
    client,
    address,
    inspections,
    sync,
    downloadedOn
  `,
  inspections: `
    id,
    project_id,
    GUID,
    template_id,
    created_date,
    inspection_types,
    facility_name,
    client_name,
    cms_inspector_name,
    inspector_id,
    compliance_id,
    municipality,
    certification_notes,
    certification_date,
    acknowledgement_date,
    compliance_date,
    status,
    createdAt,
    updatedAt,
    deletedAt,
    project,
    users,
    inspection_template,
    findings,
    questions,
    certifications,
    inspectionNumber,
    sync
  `,
  findings: `
    id,
    inspection_id,
    location,
    number,
    date_initiated,
    date_completed,
    type,
    condition,
    control_measure,
    completed_by_user_id,
    created_by_user_id,
    finding_type_id,
    initial_inspection_id,
    completed_inspection_id,
    legend_item_id,
    finding_action,
    action_note_id,
    createdAt,
    updatedAt,
    deletedAt,
    InspectionFindingModel,
    commentables,
    finding_uploads,
    action_note,
    legendName,
    sync
  `,
  questions: `
    inspection_id,
    input_label,
    input_attributes,
    input_validation_rules,
    parent_question_id,
    parent_option_id,
    question_type_id,
    sort_order,
    description,
    created_at,
    updated_at,
    deleted_at,
    answer,
    features,
    comment,
    options,
    groupName,
    sync
  `,
  certifications: `
    inspectionId,
    inspectionDOI,
    isCDOT,
    minCertificationDate,
    signatureGroups,
    groupOptions,
    userSignatures,
    attestations,
    sync
  `,
  inspectionNotes:`
      inspection_id,
      notes,
      sync`,
  findingTypeInspectionTemplate:`
      templateId,
      findingTypes
      `,
  user:`
      id,
      user_id,
      first_name,
      last_name,
      role,
      name,
      email`,
  images:`
       guid, file, type, name,sync,payload_id,project_id`,
  inspectionUploads:`
      inspection_id,
      uploads,
      sync`,
  commentPayload:`
      id++,
      inspection_id,
      question_id,
      sync`,
  inspectionTemplate:`
      template_id,
      template,
      certification`,
  positionables: `
      id,
      target_id,
      project_id,
      sync,
      start_inspection_id
      `,
  projectInspectionUploads:`
      inspection_id,
      project_id,
      data
    `
    
});



export const offlineImageCache:any = {};

// Preload images from IndexedDB into the in-memory cache.
export const preloadOfflineImages = () => {
  db.images.toArray()
    .then((images) => {
      images.forEach((image) => {
        offlineImageCache[image.guid] = image.file;
      });
    })
    .catch((error) => {
      console.error('Error preloading offline images:', error);
    });
};

export const downloadAndStoreImage = (imageUrl:string, guid:string, name:string, type:string, project_id:number) => {
    fetch(imageUrl)
      .then((response) => {
        if (response.ok) {
          return response.blob();
        } else {
          throw new Error(`Failed to fetch image from S3: ${imageUrl}`);
        }
      })
      .then((blob) => {
        // Convert blob to Base64 string
        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject('Failed to convert blob to Base64');
          reader.readAsDataURL(blob);
        });
      })
      .then((base64Image) => {
        // Save the image data to IndexedDB
        offlineImageCache[guid] = base64Image;

        return db.images.put({
          guid, // Provided GUID, or you can generate one using crypto.randomUUID()
          file: base64Image, // The Base64 image data
          type, // e.g., 'image/jpeg' or 'image/png'
          name, // e.g., 'Fuel_Sep_4.png'
          sync:{synced:true},
          project_id: project_id,
        });
      })
      .catch((error) => {
        console.error('Error downloading or storing image:', error);
      });
  };

  export const removeOfflineProjects = async (projectId:number) => {
  
      try {
        await db.projects.delete(projectId);
  
        const inspection= await db.inspections.where('project_id').equals(projectId).toArray();
  
        await db.inspections.bulkDelete(inspection.map((i:Inspection)=>i.id));
        await db.findings.bulkDelete(inspection.map((i:Inspection)=>i.findings.map((f:any)=>f.id)).flat());
        await db.questions.bulkDelete(inspection.map((i:Inspection)=>i.id));
        await db.certifications.bulkDelete(inspection.map((i:Inspection)=>i.id));
        await db.images.where('project_id').equals(projectId).delete();
        await db.inspectionNotes.bulkDelete(inspection.map((i:Inspection)=>i.id));
        await db.inspectionUploads.bulkDelete(inspection.map((i:Inspection)=>i.id));
        await db.commentPayload.bulkDelete(inspection.map((i:Inspection)=>i.id));
        await db.positionables.where('project_id').equals(projectId).delete();
        await db.projectInspectionUploads.where('project_id').equals(projectId).delete();
  
      } catch (e) {
        console.log('error', e);
        throw e;
      }
    };

    const deleteOldOfflineProjects = async (daysToKeep = 3) => {
      const now = Date.now();
      const threshold = now - daysToKeep * 24 * 60 * 60 * 1000;

      const projects = await db.projects.toArray();

      if(!projects || projects.length === 0) {

        const getAllTables = await db.tables;

       await Promise.all(
          getAllTables.map((table) => {
            return table.clear();
          }
        ))

      }

      const oldProjects = await db.projects
        .filter((project) => {
          const downloaded = new Date(project.downloadedOn).getTime();

          return downloaded < threshold;
        })
        .toArray();

      for (const project of oldProjects) {
        await removeOfflineProjects(project.id); // your existing cascade delete
      }
    };


deleteOldOfflineProjects();

// Immediately call the preload function if needed.
if (!navigator.onLine) {
  preloadOfflineImages();
}

export default db;
